import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Label } from "../../misc/Label";
import { Switch } from "../../misc/Switch";
import { Button } from "../../misc/Button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../hooks/useIsMobile";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";

const ApplicationSettings = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile(1200);

    const [isRealtorCompliant, setIsRealtorCompliant] = useState(false);
    const [isBackOnMarketEnabled, setIsBackOnMarketEnabled] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingToggleValue, setPendingToggleValue] = useState(false);
    const [pendingSetting, setPendingSetting] = useState<"nar" | "bom" | null>(
        null
    );
    const [loadingNAR, setLoadingNAR] = useState(true);
    const [loadingBOM, setLoadingBOM] = useState(true);

    useEffect(() => {
        fetchRealtorComplianceSettings();
        fetchBackOnMarketSettings();
    }, []);

    const fetchRealtorComplianceSettings = async () => {
        try {
            setLoadingNAR(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();

            const response = await axios.get(
                `${backendUrl}/settings/nar-realtor-setting`,
                { headers }
            );
            const data = response.data;
            setIsRealtorCompliant(data || false);
        } catch (error) {
            console.error(
                "Failed to fetch Realtor compliance settings:",
                error
            );
            setIsRealtorCompliant(false);
        } finally {
            setLoadingNAR(false);
        }
    };

    const updateBackOnMarketSettings = async (enabled: boolean) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();

            await axios.patch(
                `${backendUrl}/settings/back-on-market`,
                { backOnMarket: enabled },
                { headers }
            );

            setIsBackOnMarketEnabled(enabled);
        } catch (error) {
            console.error("Failed to update Back on Market settings:", error);
            toast.error(
                <CustomToast
                    message="Failed to update settings"
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
    };

    const fetchBackOnMarketSettings = async () => {
        try {
            setLoadingBOM(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();

            const response = await axios.get(
                `${backendUrl}/settings/back-on-market`,
                { headers }
            );
            const data = response.data;
            setIsBackOnMarketEnabled(data || false);
        } catch (error) {
            console.error("Failed to fetch Back on Market settings:", error);
            setIsBackOnMarketEnabled(false);
        } finally {
            setLoadingBOM(false);
        }
    };

    const updateRealtorComplianceSettings = async (enabled: boolean) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();

            await axios.patch(
                `${backendUrl}/settings/nar-realtor-setting`,
                { narRealtorSetting: enabled },
                { headers }
            );

            setIsRealtorCompliant(enabled);
        } catch (error) {
            console.error(
                "Failed to update Realtor compliance settings:",
                error
            );

            toast.error(
                <CustomToast
                    message="Failed to update settings"
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
    };

    const handleToggleClick = (setting: "nar" | "bom", newValue: boolean) => {
        setPendingSetting(setting);
        setPendingToggleValue(newValue);
        setShowConfirmation(true);
    };

    const handleConfirmToggle = async () => {
        if (pendingSetting === "nar") {
            await updateRealtorComplianceSettings(pendingToggleValue);
        } else if (pendingSetting === "bom") {
            await updateBackOnMarketSettings(pendingToggleValue);
        }
        setShowConfirmation(false);
        setPendingSetting(null);
    };

    const handleCancelToggle = () => {
        setShowConfirmation(false);
    };

    return (
        <Card
            className={`w-full max-w-4xl mx-auto ${
                isMobile ? "h-full m-0 rounded-none" : "h-auto"
            }`}
        >
            <div className="p-4">
                <Button
                    id="back-to-settings-btn"
                    onClick={() => navigate("/settings")}
                    className="mb-4 flex items-center"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                </Button>
            </div>
            <CardHeader className="mb-0 pb-0">
                <CardTitle>Application Settings</CardTitle>
                <CardDescription>
                    Configure application-wide preferences and compliance
                    settings
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-[0.8]">
                        <Label
                            htmlFor="realtor-compliance"
                            className="font-semibold text-primary"
                        >
                            NAR-Compliant Realtor® Display
                        </Label>
                        <span className="text-sm text-neutral-500 mt-2">
                            When enabled, all posts will display &quot;Realtor&quot;
                            as &quot;REALTOR®&quot; on supported social platforms to
                            meet NAR guidelines.
                        </span>
                    </div>
                    {loadingNAR ? (
                        <div className="w-12 h-6 bg-gray-200 rounded-full animate-skeleton"></div>
                    ) : (
                        <Switch
                            onClick={() =>
                                handleToggleClick("nar", !isRealtorCompliant)
                            }
                            checked={isRealtorCompliant}
                            id="realtor-compliance"
                        />
                    )}
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-[0.8]">
                        <Label
                            htmlFor="back-on-market"
                            className="font-semibold text-primary"
                        >
                            Back on Market Posts
                        </Label>
                        <span className="text-sm text-neutral-500 mt-2">
                            When enabled, if a listing moves from contract back
                            to Active, we will automatically generate and send a
                            Back on Market post.
                        </span>
                    </div>
                    {loadingBOM ? (
                        <div className="w-12 h-6 bg-gray-200 rounded-full animate-skeleton"></div>
                    ) : (
                        <Switch
                            onClick={() =>
                                handleToggleClick("bom", !isBackOnMarketEnabled)
                            }
                            checked={isBackOnMarketEnabled}
                            id="back-on-market"
                        />
                    )}
                </div>
            </CardContent>

            <ReconfirmOverlay
                isOpen={showConfirmation}
                onClose={handleCancelToggle}
                onConfirm={handleConfirmToggle}
                title={
                    pendingSetting === "nar"
                        ? `${
                              pendingToggleValue ? "Enable" : "Disable"
                          } NAR Compliance`
                        : `${
                              pendingToggleValue ? "Enable" : "Disable"
                          } Back on Market Posts`
                }
                message={
                    pendingSetting === "nar"
                        ? pendingToggleValue
                            ? 'This will change all instances of "Realtor" to "REALTOR®" on supported social platforms.'
                            : 'This will display "Realtor" as regular text without NAR compliance formatting.'
                        : pendingToggleValue
                        ? "We will automatically generate and send a Back on Market post when a listing returns to Active from Contract."
                        : "We will not generate a Back on Market post automatically."
                }
                confirmText={pendingToggleValue ? "Enable" : "Disable"}
                cancelText="Cancel"
            />
        </Card>
    );
};

export default ApplicationSettings;
